import React, { useEffect, useRef, useState } from "react";
import {
  parseIsoDateToMDYUS,
  toIsoDate,
  displayDateUS,
} from "../../../utils/dateUtils";
import { mergeFormDataWithStatusExtras } from "../../../utils/applicationFormPayload";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { withSearchParams } from "../../../hooks/useUrlTab";
import { getStandardizedSignatures } from "../../../utils/formHelpers";
import { dailyLogSchema } from "./general-forms.validation";
import EvaluationInput from "../../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import { useApplicationFormLogic } from "../../../hooks/useApplicationFormLogic";
import { request } from "../../../utils/api";
import CorrectionRequestAlert from "../../../components/Application/CorrectionRequestAlert";
import DailyLogWorkflowFooter from "./components/DailyLogWorkflowFooter";
import DailyLogSkeleton from "./components/DailyLogSkeleton";
import PropTypes from "prop-types";
import { NotesIcon } from "../../../Icons";
import {
  Field,
  FormIdentityStrip,
  FormMetaRow,
  FormPageHeader,
  HeaderControlCell,
  SHEET,
  Section,
  fieldClass,
  textareaClass,
} from "../../Patients/components/clinicalForms/formLayout";
import { PATIENT_PAGE_SHELL } from "../../Patients/patientPageLayout";
import { time12ToMinutes } from "../../Patients/components/clinicalForms/nurseNotes/nurseVisitCommon";
import { formatPatientChartName } from "../../Patients/components/clinicalForms/dailyLogEval";
import { resolveUserRoleName } from "../../../constants";
import { isDailyLogReadOnly, isOfficeReviewerUser } from "../../../utils/formPermissions";
import EmployeeNameSelect from "./components/EmployeeNameSelect";
import PatientNameSelect from "./components/PatientNameSelect";
import { useActiveEmployees } from "./hooks/useActiveEmployees";
import {
  OTHER_PATIENT_VALUE,
  useDailyLogPatients,
} from "./hooks/useDailyLogPatients";
import { getPatientById } from "../../../services/patientService";

const mapDailyLogToFormValues = (data) => ({
  ...data,
  userId: data.userId ? Number(data.userId) : undefined,
  employeeName: data.employeeName || data.applicantName || "",
  applicantName: data.applicantName || data.employeeName || "",
  title: data.title || data.filledByRole || "",
  patientId: data.patientId ? Number(data.patientId) : null,
  patientName: data.patientName || "",
  serviceDate: parseIsoDateToMDYUS(data.serviceDate),
  timeIn: data.timeIn || "",
  timeOut: data.timeOut || "",
  dailyNotes: data.dailyNotes || "",
  comments: data.comments || "",
  ...getStandardizedSignatures(data),
  applicantSignedAt: parseIsoDateToMDYUS(
    data.applicantSignedAt || data.applicantSignatureDate,
  ),
  reviewedBySignedAt: parseIsoDateToMDYUS(
    data.reviewedBySignedAt ||
      data.reviewedBySignatureDate ||
      data.adminSignatureDate,
  ),
  reviewedBySignedBy: data.reviewedBySignedBy || data.reviewedByName || "",
  patientSignatureUrl: data.patientSignatureUrl || "",
  patientSignatureImage: "",
  patientSignatureS3Key: data.patientSignatureS3Key || "",
  patientSignatureS3Url: data.patientSignatureS3Url || "",
  clearPatientSignature: false,
});

const EMPTY_DAILY_LOG_VALUES = {
  tenantId: undefined,
  userId: undefined,
  employeeName: "",
  applicantName: "",
  title: "",
  patientId: null,
  patientName: "",
  serviceDate: "",
  timeIn: "",
  timeOut: "",
  dailyNotes: "",
  applicantSignature: "",
  applicantSignedAt: "",
  reviewedBySignature: "",
  reviewedBySignedAt: "",
  reviewedBySignedBy: "",
  comments: "",
  patientSignatureUrl: "",
  patientSignatureImage: "",
  patientSignatureS3Key: "",
  patientSignatureS3Url: "",
  clearPatientSignature: false,
};

const DailyLog = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = !!id;
  const lockedPatient = location.state?.lockedPatient || null;
  const closeFormToChart = Boolean(location.state?.returnTo);
  const closePath =
    location.state?.returnTo || "/general-forms/list?tab=daily-log";
  const closeState = closeFormToChart
    ? {
        tab: location.state?.tab || "eval",
        evalSection: location.state?.evalSection || "daily-logs",
      }
    : undefined;
  const goBackFromForm = () =>
    navigate(
      closeFormToChart
        ? withSearchParams(closePath, {
            tab: closeState.tab,
            section: closeState.evalSection || null,
          })
        : closePath,
      { state: closeState },
    );
  const previewRow =
    isEditMode &&
    location.state?.dailyLog &&
    String(location.state.dailyLog.id) === String(id)
      ? location.state.dailyLog
      : null;
  const [currentStatus, setCurrentStatus] = useState(previewRow?.status || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingFlag, setSavingFlag] = useState(null);
  const [isFormReady, setIsFormReady] = useState(!isEditMode || Boolean(previewRow));
  const [isSignatureLoading, setIsSignatureLoading] = useState(isEditMode);
  const [chartPatient, setChartPatient] = useState(null);
  const submitLockRef = useRef(false);
  const ownerContextRef = useRef({
    isOffice: false,
    userFullName: "",
    userId: undefined,
  });

  const methods = useForm({
    resolver: zodResolver(dailyLogSchema),
    defaultValues: previewRow
      ? mapDailyLogToFormValues(previewRow)
      : EMPTY_DAILY_LOG_VALUES,
    mode: "onChange",
  });

  const { handleSubmit, watch, reset, setValue, getValues } = methods;
  const watchedPatientId = watch("patientId");

  const onSubmit = async (data, flag = "send_to_office", additionalData = {}) => {
    if (submitLockRef.current || isSubmitting) return;
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const { isOffice, userFullName, userId: selfUserId } =
        ownerContextRef.current;
      const actualFlag = typeof flag === "string" ? flag : "send_to_office";
      setSavingFlag(actualFlag);
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const ownerUserId = isOffice
        ? Number(merged.userId) || 0
        : Number(merged.userId) || Number(selfUserId) || 0;
      if (!ownerUserId) {
        toast.error(
          isOffice
            ? "Please select an employee"
            : "Unable to identify the employee for this daily log",
        );
        submitLockRef.current = false;
        setIsSubmitting(false);
        setSavingFlag(null);
        return;
      }
      const fullName =
        merged.employeeName || merged.applicantName || userFullName;
      const payload = {
        employeeName: fullName,
        applicantName: fullName,
        title: merged.title || "",
        patientId: lockedPatient?.id
          ? Number(lockedPatient.id)
          : merged.patientId
            ? Number(merged.patientId)
            : null,
        patientName: merged.patientName || "",
        serviceDate: merged.serviceDate
          ? toIsoDate(merged.serviceDate)
          : null,
        timeIn: merged.timeIn || null,
        timeOut: merged.timeOut || null,
        dailyNotes: merged.dailyNotes || "",
        applicantSignature: merged.applicantSignature || "",
        applicantSignedAt: merged.applicantSignedAt
          ? toIsoDate(merged.applicantSignedAt)
          : null,
        reviewedBySignature: merged.reviewedBySignature || "",
        reviewedBySignedBy: merged.reviewedBySignedBy || "",
        reviewedBySignedAt: merged.reviewedBySignedAt
          ? toIsoDate(merged.reviewedBySignedAt)
          : null,
        userId: ownerUserId,
        tenantId: merged.tenantId ? Number(merged.tenantId) : undefined,
        flag: actualFlag,
      };

      if (merged.clearPatientSignature) {
        payload.clearPatientSignature = true;
      } else if (merged.patientSignatureImage) {
        payload.patientSignatureImage = merged.patientSignatureImage;
      } else if (merged.patientSignatureS3Key || merged.patientSignatureS3Url) {
        payload.patientSignatureS3Key = merged.patientSignatureS3Key || null;
        payload.patientSignatureS3Url = merged.patientSignatureS3Url || null;
      }

      const finalComments =
        additionalData?.comments ||
        data?.comments ||
        methods.getValues("comments");
      if (finalComments) payload.comments = finalComments;

      const url = isEditMode ? `/daily-logs/${id}` : "/daily-logs";
      const method = isEditMode ? "PUT" : "POST";

      const response = await request(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success(response.message || "Daily Log processed successfully!");
        goBackFromForm();
      } else {
        toast.error(response.message || "Failed to process form!");
        submitLockRef.current = false;
        setIsSubmitting(false);
        setSavingFlag(null);
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
      submitLockRef.current = false;
      setIsSubmitting(false);
      setSavingFlag(null);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    user: currentUserData,
    signerName,
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "employeeName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose: goBackFromForm,
    onSubmit,
    currentStatus,
    skipIdentityAutoFill: true,
  });

  const isReadOnly =
    isDailyLogReadOnly(currentUserData, currentStatus) || isSubmitting;
  const sigDisabled = Boolean(isReadOnly);
  const isOffice = isOfficeReviewerUser(currentUserData);
  const { employees, loading: employeesLoading } = useActiveEmployees(isOffice);
  const { patients, loading: patientsLoading, byId: patientsById } =
    useDailyLogPatients(!lockedPatient?.id);

  useEffect(() => {
    const idToLoad = Number(lockedPatient?.id || watchedPatientId) || null;
    if (!idToLoad) {
      setChartPatient(null);
      return undefined;
    }
    let cancelled = false;
    getPatientById(idToLoad)
      .then((res) => {
        if (cancelled) return;
        const row = res?.result?.patient || res?.patient || res?.result || res;
        setChartPatient(row && row.id ? row : null);
      })
      .catch(() => {
        if (!cancelled) setChartPatient(null);
      });
    return () => {
      cancelled = true;
    };
  }, [lockedPatient?.id, watchedPatientId]);

  const userFullName = currentUserData
    ? `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`.trim() ||
      currentUserData.name ||
      ""
    : "";
  const userRoleName = resolveUserRoleName(currentUserData);
  ownerContextRef.current = {
    isOffice,
    userFullName,
    userId: currentUserData?.id,
  };

  const applyOwner = (owner, { dirty = true } = {}) => {
    if (!owner?.id) return;
    setValue("userId", Number(owner.id), {
      shouldDirty: dirty,
      shouldValidate: dirty,
    });
    setValue("employeeName", owner.name || "", {
      shouldDirty: dirty,
      shouldValidate: dirty,
    });
    setValue("applicantName", owner.name || "", { shouldDirty: false });
    setValue("title", owner.role || "", {
      shouldDirty: dirty,
      shouldValidate: dirty,
    });
  };

  useEffect(() => {
    if (!currentUserData) return;
    const tenantId = currentUserData.tenantId || currentUserData.tenant?.id;
    if (tenantId && !getValues("tenantId")) {
      setValue("tenantId", Number(tenantId), { shouldDirty: false });
    }
    if (!userRoleName && currentUserData.roleId == null) return;
    if (isOffice) return;
    applyOwner(
      {
        id: currentUserData.id,
        name: userFullName,
        role: userRoleName,
      },
      { dirty: false },
    );
    // Employee identity is always the signed-in user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserData, isOffice, isEditMode, userFullName, userRoleName]);

  useEffect(() => {
    if (isEditMode || !lockedPatient?.id) return;
    setValue("patientId", Number(lockedPatient.id), { shouldDirty: false });
    const chartName = formatPatientChartName(chartPatient) || lockedPatient.name;
    if (chartName) {
      setValue("patientName", chartName, { shouldDirty: false });
    }
  }, [isEditMode, lockedPatient, chartPatient, setValue]);

  useEffect(() => {
    if (!isEditMode) {
      setIsFormReady(true);
      setIsSignatureLoading(false);
      return undefined;
    }

    let cancelled = false;
    const usedPreview = Boolean(previewRow);
    if (!usedPreview) setIsFormReady(false);
    setIsSignatureLoading(true);

    const formPromise = request(`/daily-logs/${id}`);
    const signaturePromise = request(`/daily-logs/${id}?includeSignature=true`);

    const loadForm = async () => {
      try {
        const response = await formPromise;
        if (cancelled) return;
        if (response.status === 1 && response.result?.dailyLog) {
          const data = response.result.dailyLog;
          setCurrentStatus(data.status || "");
          if (!usedPreview || !methods.formState.isDirty) {
            reset(mapDailyLogToFormValues(data));
          }
          setIsFormReady(true);
        } else {
          toast.error("Failed to load daily log");
          setIsFormReady(true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading daily log:", error);
        toast.error("Error loading daily log");
        setIsFormReady(true);
      }

      try {
        const sigResponse = await signaturePromise;
        if (cancelled) return;
        const data = sigResponse.result?.dailyLog;
        if (
          data?.patientSignatureUrl &&
          !getValues("patientSignatureImage") &&
          !getValues("clearPatientSignature")
        ) {
          setValue("patientSignatureUrl", data.patientSignatureUrl, {
            shouldDirty: false,
          });
          if (data.patientSignatureS3Key) {
            setValue("patientSignatureS3Key", data.patientSignatureS3Key, {
              shouldDirty: false,
            });
          }
          if (data.patientSignatureS3Url) {
            setValue("patientSignatureS3Url", data.patientSignatureS3Url, {
              shouldDirty: false,
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading daily log signature:", error);
        }
      } finally {
        if (!cancelled) setIsSignatureLoading(false);
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
    // Preview from the list is only used on first mount for this id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, reset, setValue, getValues]);

  const onCloseFrom = () => {
    goBackFromForm();
  };

  const watchedPatientName = watch("patientName") || "";
  const selectedPickerPatient = watchedPatientId
    ? patientsById.get(Number(watchedPatientId))
    : null;
  const headerPatientName =
    formatPatientChartName(chartPatient) ||
    lockedPatient?.name ||
    formatPatientChartName(selectedPickerPatient) ||
    watchedPatientName;
  const headerMrn =
    chartPatient?.mrn ||
    lockedPatient?.mrn ||
    selectedPickerPatient?.mrn ||
    "";
  const headerDob = displayDateUS(
    chartPatient?.dateOfBirth ||
      lockedPatient?.dateOfBirth ||
      selectedPickerPatient?.dateOfBirth,
  );
  const timeError =
    methods.formState.errors.timeOut?.message ||
    methods.formState.errors.timeIn?.message ||
    "";
  const metaForm = {
    filledByName: watch("employeeName") || userFullName || "",
    filledByRole: watch("title") || "",
    status: currentStatus,
    scheduleDate: watch("serviceDate") || null,
    reviewedByName: watch("reviewedBySignedBy") || "",
  };

  if (isEditMode && !isFormReady) {
    return <DailyLogSkeleton />;
  }

  return (
    <div className={PATIENT_PAGE_SHELL}>
      <FormPageHeader
        patientName={headerPatientName}
        mrn={headerMrn}
        status={currentStatus || "pending"}
        onBack={onCloseFrom}
        backLabel={closeFormToChart ? "Back to patient" : "Back to Daily Logs"}
      />

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Daily Log
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Daily visit documentation for home health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit((data) => onSubmit(data, "send_to_office"))}
            className="space-y-3"
          >
            <FormIdentityStrip
              patientName={headerPatientName}
              mrn={headerMrn}
              dateOfBirth={headerDob}
              form={metaForm}
              dateLabel="Service Date"
              dateValue={watch("serviceDate") || ""}
              patientSlot={
                lockedPatient?.id ? null : (
                  <HeaderControlCell label="Patient Name">
                    <PatientNameSelect
                      compact
                      value={
                        watchedPatientId
                          ? watchedPatientId
                          : OTHER_PATIENT_VALUE
                      }
                      displayName={watchedPatientName}
                      patients={patients}
                      loading={patientsLoading}
                      disabled={sigDisabled}
                      onSelect={(next) => {
                        if (next.other) {
                          setValue("patientId", null, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          if (!watch("patientName")) {
                            setValue("patientName", "", {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                          return;
                        }
                        setValue("patientId", next.id, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                        setValue("patientName", next.name || "", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    />
                  </HeaderControlCell>
                )
              }
              caregiverSlot={
                isOffice && !sigDisabled ? (
                  <HeaderControlCell label="Caregiver Name">
                    <EmployeeNameSelect
                      compact
                      value={watch("userId")}
                      displayName={watch("employeeName") || ""}
                      employees={employees}
                      loading={employeesLoading}
                      disabled={sigDisabled}
                      onSelect={applyOwner}
                    />
                  </HeaderControlCell>
                ) : null
              }
              dateSlot={
                <HeaderControlCell label="Service Date">
                  <InputDatePicker
                    name="serviceDate"
                    formatSeparator="/"
                    allowManualEntry
                    compact
                    disabled={sigDisabled}
                    className="min-w-0 w-full"
                    inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                  />
                </HeaderControlCell>
              }
              visitMeta={{
                showBillingCode: false,
                timeIn: watch("timeIn") || "",
                timeOut: watch("timeOut") || "",
                canEdit: !sigDisabled,
                onTimeInChange: (next) => {
                  const out = watch("timeOut") || "";
                  const start = next ? time12ToMinutes(next) : null;
                  const end = out ? time12ToMinutes(out) : null;
                  const clearOut =
                    start != null && end != null && end <= start;
                  setValue("timeIn", next, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  if (clearOut) {
                    setValue("timeOut", "", {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }
                },
                onTimeOutChange: (next) =>
                  setValue("timeOut", next, {
                    shouldDirty: true,
                    shouldValidate: true,
                  }),
                error: timeError,
              }}
            />

            {!lockedPatient?.id && !watchedPatientId ? (
              <Field label="Other patient name (not linked)">
                <EvaluationInput
                  name="patientName"
                  className="w-full"
                  inputClassName={fieldClass}
                  disabled={sigDisabled}
                  placeholder="Type patient name (not linked)"
                />
              </Field>
            ) : null}
            {methods.formState.errors.patientName?.message ? (
              <p className="text-[10px] font-medium text-rose-600">
                {methods.formState.errors.patientName.message}
              </p>
            ) : null}
            {methods.formState.errors.employeeName?.message ? (
              <p className="text-[10px] font-medium text-rose-600">
                {methods.formState.errors.employeeName.message}
              </p>
            ) : null}

            <FormMetaRow form={metaForm} />
            <CorrectionRequestAlert
              status={currentStatus}
              comments={watch("comments")}
            />

            <Section icon={NotesIcon} title="1. Daily Notes">
              <Field label="Notes">
                <textarea
                  className={`${textareaClass} min-h-[120px]`}
                  placeholder="Enter daily log notes…"
                  disabled={sigDisabled}
                  {...methods.register("dailyNotes")}
                />
              </Field>
            </Section>

            <DailyLogWorkflowFooter
              isOffice={isOffice}
              isReadOnly={isReadOnly}
              currentStatus={currentStatus || "pending"}
              employeeName={watch("employeeName") || userFullName || ""}
              reviewerName={
                isOffice
                  ? watch("reviewedBySignedBy") ||
                    userFullName ||
                    signerName ||
                    ""
                  : watch("reviewedBySignedBy") || ""
              }
              saving={savingFlag}
              onSave={handleSave}
              onSendToOffice={(data, status) => onSubmit(data, status)}
              onStatusUpdate={handleStatusAction}
              patientNameLocked={Boolean(lockedPatient?.id || watchedPatientId)}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

DailyLog.propTypes = {
  onClose: PropTypes.func,
};

export default DailyLog;
